import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from 'angular-toastify';
import {
  QuotaCardAdvanceActivateCode,
  QuotaCardAdvanceDetail,
} from '../../../../../core/models/card-quota/quota-card-advance.model';
import { MapperQuotaCardAdvanceService } from '../../../../../core/mappers/card-quota/mapper-quota-card-advance.service';
import { ApiCardQuotaService } from '../../../../../core/services/api/card-quota/api-card-quota.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { downloadActivateCodesExcel } from '../../../../../core/utils/activate-codes-excel-export.util';

@Component({
  selector: 'app-modal-quota-card-advance-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './modal-quota-card-advance-detail.component.html',
  styleUrl: './modal-quota-card-advance-detail.component.css',
})
export class ModalQuotaCardAdvanceDetailComponent implements OnInit {
  @Input() advanceId = 0;
  @Output() close = new EventEmitter<void>();

  detail: QuotaCardAdvanceDetail | null = null;
  activateCodes: QuotaCardAdvanceActivateCode[] = [];
  isLoading = signal(false);
  isLoadingCodes = signal(false);
  isExporting = signal(false);

  private readonly api = inject(ApiCardQuotaService);
  private readonly mapper = inject(MapperQuotaCardAdvanceService);
  private readonly toast = inject(ToastService);
  private readonly siteState = inject(SiteStateService);

  statusLabel = (s: number) => this.mapper.statusLabel(s);

  get isExecuted(): boolean {
    return this.detail?.status === 3;
  }

  get usedCodeCount(): number {
    return this.activateCodes.filter((c) => c.isUsed).length;
  }

  get unusedCodeCount(): number {
    return this.activateCodes.filter((c) => !c.isUsed).length;
  }

  statusBadgeClass(status: number): string {
    const map: Record<number, string> = {
      1: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
      2: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      3: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      4: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      5: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
      6: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    };
    return map[status] ?? 'bg-slate-100 text-slate-600';
  }

  ngOnInit(): void {
    if (this.advanceId > 0) {
      this.loadDetail();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  exportExcel(): void {
    if (!this.detail || this.activateCodes.length === 0) return;

    this.isExporting.set(true);
    const d = this.detail;
    downloadActivateCodesExcel(this.activateCodes, {
      advanceId: d.id,
      advanceName: d.name,
      siteName: this.siteState.selectedSite()?.name ?? null,
      clientName: d.clientName,
      vehicleTypeName: d.vehicleTypeName,
      cardTypeName: d.cardTypeName,
      qty: d.qty,
      startDate: d.startDate,
      endDate: d.endDate,
    })
      .then(() => this.toast.success('ส่งออก Excel สำเร็จ'))
      .catch(() => this.toast.error('ส่งออก Excel ล้มเหลว'))
      .finally(() => this.isExporting.set(false));
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getQuotaCardAdvanceById(this.advanceId).subscribe({
      next: (res) => {
        this.detail = this.mapper.mapDetail(res as QuotaCardAdvanceDetail & Record<string, unknown>);
        this.isLoading.set(false);
        if (this.detail.status === 3) {
          this.loadActivateCodes();
        }
      },
      error: () => {
        this.toast.error('โหลดรายละเอียดไม่สำเร็จ');
        this.isLoading.set(false);
      },
    });
  }

  private loadActivateCodes(): void {
    this.isLoadingCodes.set(true);
    this.api.getQuotaCardAdvanceActivateCodes(this.advanceId).subscribe({
      next: (res) => {
        this.activateCodes = this.mapper.mapActivateCodes(res ?? []);
        this.isLoadingCodes.set(false);
      },
      error: (err) => {
        this.activateCodes = [];
        this.isLoadingCodes.set(false);
        this.toast.error(err?.error?.message || 'โหลดรหัส activate ล้มเหลว');
      },
    });
  }
}
